// Sales-bucket Opportunities mode — the host for the reusable Opportunity
// Engine that replaces the fillable PDF red iron sales process and serves
// as the foundation for future complex product sales workflows.
//
// Spec: docs/GPT_To_Repo.md (Product_Order_Process v1.0).
// Schema: docs/design/sql_schema_opportunity.md.
//
// Phase 1 build sequence:
//   - Slice 1: registries wired, opp_* tables, opportunity-documents bucket,
//     server auth + dispatcher substrate in place.
//   - Slice 2 (this slice): opportunity list + workspace shell + pricing engine.
//   - Slice 3: documents (sign / commit / read) + revisions.
//   - Slice 4: proposal package PDF generation (with org logo embedded).

import { useState } from "react";
import { OpportunityListView } from "@/components/opportunity/OpportunityListView";
import { OpportunityCreateModal } from "@/components/opportunity/OpportunityCreateModal";
import { OpportunityWorkspace } from "@/components/opportunity/OpportunityWorkspace";
import { consumePendingOpportunityOpen, type PendingOpportunityTab } from "@/lib/opportunity/pendingOpportunityOpen";

export function OpportunitiesModePanel() {
  const [pendingOpen] = useState(() => consumePendingOpportunityOpen());
  const [openId, setOpenId] = useState<string | null>(pendingOpen?.id ?? null);
  const [initialTab, setInitialTab] = useState<PendingOpportunityTab | undefined>(pendingOpen?.tab);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);

  return (
    <div className="w-full text-left space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Opportunities</h2>
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Customer-associated sales opportunities scoped to a product type — capture pricing,
          supporting documents, revisions, and branded proposal packages.
        </p>
      </div>

      {openId ? (
        <OpportunityWorkspace
          opportunityId={openId}
          initialTab={initialTab}
          onBack={() => {
            setOpenId(null);
            setInitialTab(undefined);
            setRefreshKey((k) => k + 1);
          }}
        />
      ) : (
        <OpportunityListView
          onOpenOpportunity={(id) => setOpenId(id)}
          onNewOpportunity={() => setCreating(true)}
          refreshKey={refreshKey}
        />
      )}

      <OpportunityCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          setRefreshKey((k) => k + 1);
          setOpenId(id);
        }}
      />
    </div>
  );
}
